// ISDP CUSTOM: Pure builders for the one-click "connect your AI tool" snippets.
// Given the DevHub base URL and a freshly-minted PAT, produce ready-to-paste
// configuration for the common MCP clients with the token already substituted.
//
// This module is intentionally pure (no React, no APIs) so it is trivially unit-
// and e2e-inspectable and holds NO state — the caller (ConnectDialog) owns the
// token's lifetime and never persists it.

export type ConnectClient = 'claudeCode' | 'cursor' | 'claudeDesktop' | 'vscode';

export interface ConnectSnippets {
  /** Claude Code — a single `claude mcp add` shell command. */
  claudeCode: string;
  /** Cursor — a `mcpServers` JSON entry for `~/.cursor/mcp.json`. */
  cursor: string;
  /** Claude Desktop — a `mcpServers` JSON entry for the desktop config file. */
  claudeDesktop: string;
  /** VS Code — a `servers` JSON entry for `.vscode/mcp.json`. */
  vscode: string;
}

// The DevHub MCP server name presented to clients. Kept stable so a user's config
// reads sensibly regardless of the catalog id of the underlying listing.
const DEFAULT_SERVER_KEY = 'draeger-devhub';

/**
 * Build the connect snippets for every supported client.
 *
 * @param base       DevHub base URL (from `backend.baseUrl` / `app.baseUrl`), no trailing slash required.
 * @param token      the raw PAT (`dhpat_…`) held only in memory by the caller.
 * @param serverKey  optional server name to use in the config (defaults to `draeger-devhub`).
 */
export function buildConnectSnippets(
  base: string,
  token: string,
  serverKey: string = DEFAULT_SERVER_KEY,
): ConnectSnippets {
  const httpEndpoint = `${(base || '').replace(/\/+$/, '')}/api/mcp/http`;
  const key = serverKey || DEFAULT_SERVER_KEY;

  const claudeCode = `claude mcp add --transport http ${key} ${httpEndpoint} --header "Authorization: Bearer ${token}"`;

  const cursor = `{
  "mcpServers": {
    "${key}": {
      "url": "${httpEndpoint}",
      "headers": { "Authorization": "Bearer ${token}" }
    }
  }
}`;

  const claudeDesktop = `{
  "mcpServers": {
    "${key}": {
      "type": "http",
      "url": "${httpEndpoint}",
      "headers": { "Authorization": "Bearer ${token}" }
    }
  }
}`;

  const vscode = `{
  "servers": {
    "${key}": {
      "type": "http",
      "url": "${httpEndpoint}",
      "headers": { "Authorization": "Bearer ${token}" }
    }
  }
}`;

  return { claudeCode, cursor, claudeDesktop, vscode };
}
